import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Typography, CircularProgress, Link } from '@mui/material';
import {
  ExpandMore, ExpandLess, Storage, CheckCircle, ErrorOutline, Psychology, Code,
} from '@mui/icons-material';
import type { AgentStep } from '../../lib/chatTypes';

interface ThinkingStepsProps {
  steps: AgentStep[];
  active: boolean; // true while the turn is still streaming
}

const StatusIcon: React.FC<{ status: AgentStep['status'] }> = ({ status }) => {
  if (status === 'running') return <CircularProgress size={13} />;
  if (status === 'error') return <ErrorOutline sx={{ fontSize: 15 }} color="error" />;
  return <CheckCircle sx={{ fontSize: 15 }} color="success" />;
};

// One process step: a friendly line + timestamp, with the raw SQL tucked behind
// a "show query" toggle so the internal query isn't shown by default.
const StepRow: React.FC<{ step: AgentStep; index: number; locale: string }> = ({ step, index, locale }) => {
  const { t } = useTranslation();
  const [showSql, setShowSql] = useState(false);

  const label =
    step.status === 'running' ? t('agent.queryingDb')
      : step.status === 'error' ? t('agent.queryFailed')
        : t('agent.rowCount', { count: step.rowCount ?? 0 });

  const time = step.at ? new Date(step.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
      <Box sx={{ mt: 0.25 }}><StatusIcon status={step.status} /></Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Storage sx={{ fontSize: 13 }} color="action" />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{t('agent.step', { n: index + 1 })}</Typography>
          <Typography variant="caption" color="text.secondary">— {label}</Typography>
          {time && (
            <Typography variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums' }}>· {time}</Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Link component="button" type="button" underline="hover" variant="caption"
            onClick={() => setShowSql((v) => !v)}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}>
            <Code sx={{ fontSize: 13 }} />
            {showSql ? t('agent.hideQuery') : t('agent.showQuery')}
          </Link>
        </Box>
        <Collapse in={showSql}>
          <Box component="pre" sx={{
            m: 0, mt: 0.5, p: 0.75, borderRadius: 0.75, bgcolor: 'grey.900', color: 'grey.100',
            fontSize: '0.68rem', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            direction: 'ltr', textAlign: 'left',
          }}>{step.sql}</Box>
        </Collapse>
        {step.error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>{step.error}</Typography>}
      </Box>
    </Box>
  );
};

/**
 * A compact, collapsible "thinking" log of the agent's process. The internal
 * queries are hidden by default — each step shows a friendly line + timestamp,
 * with an optional "show query" toggle to reveal the SQL for that step.
 * Auto-expanded while running, auto-collapses when the answer is ready.
 */
const ThinkingSteps: React.FC<ThinkingStepsProps> = ({ steps, active }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-US';
  const [open, setOpen] = useState(active);
  const [touched, setTouched] = useState(false);

  React.useEffect(() => {
    if (!touched) setOpen(active);
  }, [active, touched]);

  if (!steps || steps.length === 0) return null;

  const summary = active ? t('agent.working') : t('agent.stepsDone', { count: steps.length });

  return (
    <Box sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'action.hover' }}>
      <Box
        onClick={() => { setOpen((o) => !o); setTouched(true); }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, cursor: 'pointer', userSelect: 'none' }}
      >
        {active ? <CircularProgress size={14} /> : <Psychology sx={{ fontSize: 16 }} color="action" />}
        <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.secondary' }}>
          {summary}
        </Typography>
        {open ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1, pb: 1, pt: 0.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {steps.map((s, i) => (
            <StepRow key={i} step={s} index={i} locale={locale} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ThinkingSteps;
